import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Tabs, Tab, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { showToast } from '../../hooks/useToast';

const CourseComparison = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchComparison();
    }, []);

    const fetchComparison = async () => {
        try {
            const response = await api.get('/courses/compare');
            setData(response.data);
        } catch (error) {
            showToast('Error al cargar la comparación de cursos', 'danger');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Analizando comparativa de cursos...</p>
            </div>
        );
    }

    if (!data) return <Alert variant="danger">No se pudieron cargar los datos.</Alert>;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" size="sm" className="mb-2" onClick={() => navigate('/courses')}>
                        <i className="bi bi-arrow-left me-1"></i> Volver a Cursos
                    </Button>
                    <h2>Comparativa de Cursos (Subjects)</h2>
                </div>
                <div>
                    <Button variant="primary" onClick={fetchComparison}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Actualizar
                    </Button>
                </div>
            </div>

            {data.updated > 0 && (
                <Alert variant="success" className="mb-4">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Se vincularon automáticamente <strong>{data.updated}</strong> cursos que coincidían por ID Number.
                </Alert>
            )}

            <div className="row mb-4">
                <div className="col-md-4">
                    <Card className="text-center h-100 border-success">
                        <Card.Body>
                            <h3 className="tex-success">{data.matches.length}</h3>
                            <div className="text-muted">Sincronizados</div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-4">
                    <Card className="text-center h-100 border-warning">
                        <Card.Body>
                            <h3 className="text-warning">{data.localOnly.length}</h3>
                            <div className="text-muted">Solo en Local</div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-4">
                    <Card className="text-center h-100 border-info">
                        <Card.Body>
                            <h3 className="text-info">{data.moodleOnly.length}</h3>
                            <div className="text-muted">Solo en Moodle</div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <Tabs defaultActiveKey="local-only" className="mb-4">
                <Tab eventKey="local-only" title={<span className="text-warning">Solo en Local ({data.localOnly.length})</span>}>
                    <Card>
                        <Card.Header className="bg-white">
                            <div className="text-muted small">
                                Cursos en la base de datos local que no tienen correspondencia en Moodle (según ID Number).
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>ID</th>
                                        <th>Nombre</th>
                                        <th>Sección</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.localOnly.map(subject => (
                                        <tr key={subject.id}>
                                            <td>{subject.id}</td>
                                            <td>{subject.name}</td>
                                            <td>{subject.section_name}</td>
                                            <td>
                                                <Badge bg="warning" text="dark">Pendiente</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                    {data.localOnly.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted">
                                                No hay cursos pendientes en local.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="moodle-only" title={<span className="text-info">Solo en Moodle ({data.moodleOnly.length})</span>}>
                    <Card>
                        <Card.Header className="bg-white">
                            <div className="text-muted small">
                                Cursos en Moodle que no coinciden con ningún curso local (ID Number desconocido).
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>ID</th>
                                        <th>Nombre Corto</th>
                                        <th>Nombre Completo</th>
                                        <th>ID Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.moodleOnly.map(course => (
                                        <tr key={course.id}>
                                            <td>{course.id}</td>
                                            <td>{course.shortname}</td>
                                            <td>{course.fullname}</td>
                                            <td>{course.idnumber || <span className="text-muted">-</span>}</td>
                                        </tr>
                                    ))}
                                    {data.moodleOnly.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted">
                                                No hay cursos extraños en Moodle.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="matches" title={<span className="text-success">Sincronizados ({data.matches.length})</span>}>
                    <Card>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Local</th>
                                        <th>Moodle</th>
                                        <th>ID Number</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.matches.map((match, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <div>{match.local.name}</div>
                                                <small className="text-muted">{match.local.section_name}</small>
                                            </td>
                                            <td>
                                                <div>{match.moodle.fullname}</div>
                                                <small className="text-muted">{match.moodle.shortname}</small>
                                            </td>
                                            <td>{match.moodle.idnumber}</td>
                                            <td>
                                                <Badge bg="success">Sincronizado</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </div>
    );
};

export default CourseComparison;
